import { useState, useEffect } from 'react';
import { subscribeToSignupStatus } from '@/services/configService';
import { useTheme } from '@/hooks/useTheme';
import {
  LandingNav,
  HeroSection,
  CourseCard,
  FeatureCard,
  CTASection,
  LandingFooter,
} from '@/components/landing';
import {
  AiBrainIcon,
  SparkleIcon,
  ChartBarIcon,
  CalculatorIcon,
} from '@/components/icons';
import '@/styles/landing.css';

/**
 * Landing Page
 *
 * Public landing page for ClosedLoopDiving featuring:
 * - Hero section with CLD branding and demo access
 * - Course overview cards
 * - Feature highlights with SVG icons
 * - Light/dark theme toggle
 * - Professional animations and scroll reveals
 */
export function Landing() {
  const [signupEnabled, setSignupEnabled] = useState(true);
  const { isDark, toggleTheme } = useTheme();

  // Subscribe to signup status for real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToSignupStatus((enabled) => {
      setSignupEnabled(enabled);
    });

    return () => unsubscribe();
  }, []);

  const courses = [
    {
      id: 'helitrox',
      title: 'Helitrox Fundamentals',
      description:
        'Master the essentials of helium-based gas mixtures for extended range diving. Learn MOD calculations, EAD, and gas planning.',
      lessons: 17,
      duration: '8-10 hours',
      level: 'Intermediate',
    },
    {
      id: 'trimix',
      title: 'Trimix Deep Diving',
      description:
        'Advanced trimix theory for technical deep diving. Covers decompression planning, gas switches, and emergency procedures.',
      lessons: 20,
      duration: '12-15 hours',
      level: 'Advanced',
      comingSoon: true,
    },
    {
      id: 'rebreather',
      title: 'CCR Fundamentals',
      description:
        'Introduction to closed-circuit rebreathers. Understand oxygen management, CO2 scrubbing, and bailout planning.',
      lessons: 15,
      duration: '10-12 hours',
      level: 'Advanced',
      comingSoon: true,
    },
  ];

  const features = [
    {
      icon: <AiBrainIcon size={48} />,
      title: 'AI-Powered Learning',
      description: 'Cutting-edge technology reinforces critical concepts for technical diver safety.',
    },
    {
      icon: <SparkleIcon size={48} />,
      title: 'AI-Powered Quizzes',
      description: 'Dynamic questions generated by AI adapt to your learning, with instant feedback.',
    },
    {
      icon: <ChartBarIcon size={48} />,
      title: 'Track Your Progress',
      description: 'Visual progress tracking with streaks, badges, and detailed analytics.',
    },
    {
      icon: <CalculatorIcon size={48} />,
      title: 'Built-in Calculator',
      description: 'Integrated dive calculator for MOD, EAD, and partial pressure calculations.',
    },
  ];

  return (
    <div className={`landing-page ${isDark ? 'landing-dark' : ''}`}>
      {/* Navigation */}
      <LandingNav
        signupEnabled={signupEnabled}
        isDark={isDark}
        onThemeToggle={toggleTheme}
      />

      {/* Hero Section */}
      <HeroSection />

      {/* Course Cards Section */}
      <section className="landing-section landing-section--alt">
        <h2 className="landing-section__title animate-fadeInUp">Available Courses</h2>
        <div className="landing-section__grid">
          {courses.map((course, index) => (
            <CourseCard
              key={course.id}
              {...course}
              className={`animate-fadeInUp stagger-${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="landing-section">
        <h2 className="landing-section__title animate-fadeInUp">Why ClosedLoopDiving?</h2>
        <div className="landing-section__grid landing-section__grid--features">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              {...feature}
              className={`animate-fadeInUp stagger-${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        signupEnabled={signupEnabled}
        className="landing-section--alt"
      />

      {/* Footer */}
      <LandingFooter />
    </div>
  );
}
